import { FC, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Input, Button } from '@/components/ui';
import type { EditUserFormProps, UpdateUserPayload } from '@/features/users/types';

const schema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters'),
  email: z.string().email('Please provide a valid email address'),
  company: z.string().optional().or(z.literal('').transform(() => undefined)),
  phone: z.string().optional().or(z.literal('').transform(() => undefined)),
});

type FormValues = z.infer<typeof schema>;

export const EditUserForm: FC<EditUserFormProps> = ({ user, onSubmit, onCancel }) => {
  const { handleSubmit, reset, setValue, formState: { errors, isSubmitting } } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: user.name,
      email: user.email,
      company: user.company || '',
      phone: user.phone || '',
    },
  });

  useEffect(() => {
    reset({
      name: user.name,
      email: user.email,
      company: user.company || '',
      phone: user.phone || '',
    });
  }, [user, reset]);

  const handleFormSubmit = async (values: FormValues) => {
    const payload: UpdateUserPayload = {
      name: values.name,
      company: values.company || '',
      phone: values.phone || '',
    };
    await onSubmit(payload);
  };

  return (
    <div className="fixed inset-0 bg-gray-900/50 flex items-center justify-center z-50">
      <div className="bg-white w-full max-w-md mx-4">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Edit User</h2>
        </div>

        <form onSubmit={(e) => e.preventDefault()} className="px-6 py-6 space-y-4">
          <Input
            label="Name"
            fullWidth
            error={errors?.name?.message as string}
            defaultValue={user.name}
            onChange={(e) => setValue('name', e.target.value)}
          />

          <Input
            label="Email"
            type="email"
            fullWidth
            defaultValue={user.email}
            disabled
            readOnly
            className="bg-gray-100"
          />

          <Input
            label="Company"
            fullWidth
            error={errors?.company?.message as string}
            defaultValue={user.company || ''}
            onChange={(e) => setValue('company', e.target.value)}
          />

          <Input
            label="Phone"
            type="tel"
            fullWidth
            error={errors?.phone?.message as string}
            defaultValue={user.phone || ''}
            onChange={(e) => {
              const value = e.target.value.replace(/[^0-9+\-()\s]/g, '');
              setValue('phone', value);
              e.target.value = value;
            }}
          />
        </form>

        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-2">
          <Button
            variant="secondary"
            onClick={onCancel}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit(handleFormSubmit)}
            isLoading={isSubmitting}
          >
            {isSubmitting ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </div>
    </div>
  );
};

